import {
  isAudio,
  isImage,
  isText,
  isSVG,
  isVideo,
  getMimeType,
  getMimeBase,
  getFilename,
  getFileExtension,
} from '@nx-superprep/utils';

/**
 * Represents a reference to a backend stored asset.
 */
export class Asset {
  /** The URL of the asset. */
  get url() {
    return this._url;
  }

  /** The base MIME type of the asset. */
  get mimeBase() {
    return getMimeBase(this.mimeType);
  }

  /** Checks if the asset is an audio file. */
  get isAudio() {
    return isAudio(this.mimeType);
  }

  /** Checks if the asset is an image file. */
  get isImage() {
    return isImage(this.mimeType);
  }

  /** Checks if the asset is a text file. */
  get isText() {
    return isText(this.mimeType);
  }

  /** Checks if the asset is an SVG file. */
  get isSVG() {
    return isSVG(this.mimeType);
  }

  /** Checks if the asset is a video file. */
  get isVideo() {
    return isVideo(this.mimeType);
  }

  /** Checks if the asset is valid, i.e., it has a URL. */
  get isValid(): boolean {
    return Boolean(this.url);
  }

  /** The name of the asset. */
  name!: string

  /** The MIME type of the asset. */
  mimeType!: string

  /** The asset type. */
  assetType!: string

  /** The duration of the asset. */
  duration = 0

  /** The names of the lessons associated with the asset. */
  private _lessonNames!: string[];
  get lessonNames(): string[] {
    return this._lessonNames;
  }
  set lessonNames(value: string[]) {
    this._lessonNames = value;
  }

  protected _url?: string;

  /**
   * Creates an instance of Asset.
   * @param options Optional parameters to initialize the asset.
   */
  constructor(options: Partial<Asset>) {
    if (!options) { return }
    const _extension = getFileExtension(options.name ?? '');
    this.name = options.name ?? getFilename(options.name ?? '');
    this.mimeType = options.mimeType ?? '';
    if (_extension) {
      const mimeType = getMimeType(_extension);
      if (mimeType && this.mimeType !== mimeType) {
        this.mimeType = mimeType;
      }
    }
    this.assetType = options.assetType ?? getMimeBase(this.mimeType);
    this._url = options.url;
  }

  /**
   * Checks if this asset is the same as another asset.
   * @param item The asset to compare.
   * @returns True if the assets have the same name and type, otherwise false.
   */
  isSameAsset(item: Asset): boolean {
    return this.isSameName(item) && this.isSameType(item)
  }

  /**
   * Checks if this asset has the same name as another asset.
   * @param item The asset to compare.
   * @returns True if both assets have the same name, otherwise false.
   */
  isSameName(item: Asset): boolean {
    return Boolean(this.name && item.name && this.name === item.name);
  }

  /**
   * Checks if this asset has the same type as another asset.
   * @param item The asset to compare.
   * @returns True if both assets have the same type, otherwise false.
   */
  isSameType(item: Asset): boolean {
    return Boolean(this.mimeBase && item.mimeBase && this.mimeBase === item.mimeBase);
  }

  /**
   * Checks if this asset is a duplicate of another asset (having the same name).
   * @param item The asset to compare.
   * @returns True if this asset has the same name as the other asset, otherwise false.
   */
  isDuplicate(item: Asset): boolean {
    return this.isSameName(item)
  }
}

/**
 * Extracts the asset name from the filename.
 * @param name The filename.
 * @returns The extracted asset name.
 */
export const assetNameFromFilename = (name: string): string => name?.replace(/@?((1|2|3)x)?(~\w+)?(\..*$)?/g, '');
